import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, CheckCircle2, ListChecks, AlignLeft, Info } from "lucide-react";

const uid = () => "q" + Math.random().toString(36).slice(2, 9);

export default function EditAssignment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [reading, setReading] = useState("");
  const [chapter, setChapter] = useState("");
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/assignments/${id}`).then((r) => {
      const d = r.data;
      setA(d);
      setTitle(d.title || "");
      setDue((d.due_date || "").slice(0, 10));
      setReading(String(d.reading_level ?? ""));
      setChapter(d.chapter || "");
      setQuestions((d.lesson?.questions || []).map((q) => ({ ...q, id: q.id || uid() })));
    });
  }, [id]);

  const upd = (qid, patch) => setQuestions((qs) => qs.map((q) => (q.id === qid ? { ...q, ...patch } : q)));
  const removeQ = (qid) => setQuestions((qs) => qs.filter((q) => q.id !== qid));
  const addQ = (type) => setQuestions((qs) => [...qs, type === "multiple_choice"
    ? { id: uid(), type: "multiple_choice", question: "", options: ["", ""], correct_answer: "", points: 2, rubric: "", expected_concepts: [] }
    : { id: uid(), type: "short_answer", question: "", points: 10, rubric: "", expected_concepts: [] }]);

  const setOption = (qid, i, val) => setQuestions((qs) => qs.map((q) => {
    if (q.id !== qid) return q;
    const options = [...(q.options || [])];
    const was = options[i];
    options[i] = val;
    // keep correct_answer in sync if it pointed to the edited option
    const correct_answer = q.correct_answer === was ? val : q.correct_answer;
    return { ...q, options, correct_answer };
  }));
  const addOption = (qid) => setQuestions((qs) => qs.map((q) => (q.id === qid ? { ...q, options: [...(q.options || []), ""] } : q)));
  const removeOption = (qid, i) => setQuestions((qs) => qs.map((q) => {
    if (q.id !== qid) return q;
    const opt = (q.options || [])[i];
    return { ...q, options: (q.options || []).filter((_, x) => x !== i), correct_answer: q.correct_answer === opt ? "" : q.correct_answer };
  }));

  const save = async (goList) => {
    if (!title.trim()) return toast.error("Give the assignment a title.");
    for (const q of questions) {
      if (!q.question.trim()) return toast.error("Every question needs text.");
      if (q.type === "multiple_choice") {
        const opts = (q.options || []).filter((o) => o.trim());
        if (opts.length < 2) return toast.error("Multiple-choice questions need at least 2 options.");
        if (!q.correct_answer || !opts.includes(q.correct_answer)) return toast.error("Pick the correct answer for each multiple-choice question.");
      }
    }
    setSaving(true);
    try {
      await api.patch(`/assignments/${id}`, {
        title, due_date: due || null, reading_level: reading, chapter,
        questions: questions.map((q) => ({ ...q, points: Number(q.points) || 0 })),
      });
      toast.success("Assignment updated!");
      if (goList) navigate("/parent/manage-assignments");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!a) return <div><TopBar subtitle="Edit assignment" /><p className="p-10 text-[#6B7280]">Loading…</p></div>;

  return (
    <div>
      <TopBar subtitle="Edit assignment" />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <button onClick={() => navigate("/parent/manage-assignments")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B] mb-6"><ArrowLeft size={16} /> Manage Assignments</button>
        <h1 className="font-head text-4xl font-bold text-[#3D405B] mb-1">Edit assignment</h1>
        <p className="text-[#6B7280] mb-8">{a.subject}</p>

        <div className="rounded-2xl bg-white border border-[#EAE6DF] p-6 space-y-5">
          <div>
            <label className="text-sm font-medium text-[#3D405B]">Title</label>
            <input data-testid="edit-asg-title" value={title} onChange={(e) => setTitle(e.target.value)} className="inp" />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-[#3D405B]">Due date</label>
              <input data-testid="edit-asg-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} className="inp" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#3D405B]">Reading level</label>
              <input data-testid="edit-asg-reading" value={reading} onChange={(e) => setReading(e.target.value)} className="inp" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#3D405B]">Chapter</label>
              <input data-testid="edit-asg-chapter" value={chapter} onChange={(e) => setChapter(e.target.value)} className="inp" />
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <h2 className="font-head text-lg font-semibold text-[#3D405B]">Questions <span className="text-sm font-normal text-[#6B7280]">· {questions.length}</span></h2>
          <div className="flex gap-2">
            <button data-testid="add-mc-btn" onClick={() => addQ("multiple_choice")} className="flex items-center gap-1.5 text-sm rounded-full border border-[#EAE6DF] px-4 py-2 hover:border-[#81B29A] transition-colors"><ListChecks size={15} /> Multiple choice</button>
            <button data-testid="add-written-btn" onClick={() => addQ("short_answer")} className="flex items-center gap-1.5 text-sm rounded-full border border-[#EAE6DF] px-4 py-2 hover:border-[#81B29A] transition-colors"><AlignLeft size={15} /> Written</button>
          </div>
        </div>

        <div className="mt-3 flex items-start gap-2 rounded-xl bg-[#F4A261]/10 border border-[#F4A261]/30 p-3 text-sm text-[#3D405B]">
          <Info size={16} className="mt-0.5 shrink-0 text-[#F4A261]" />
          <span>Editing questions changes what students see going forward. It won't alter a grade a student has already earned — reset that assignment in the gradebook if you want them to redo it.</span>
        </div>

        <div className="mt-4 space-y-4">
          {questions.map((q, i) => (
            <div key={q.id} data-testid={`edit-q-${i}`} className="rounded-2xl bg-white border border-[#EAE6DF] p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="stat text-xs text-[#6B7280]">Q{i + 1}</span>
                  <select data-testid={`edit-q-type-${i}`} value={q.type === "multiple_choice" ? "multiple_choice" : "short_answer"}
                    onChange={(e) => upd(q.id, e.target.value === "multiple_choice"
                      ? { type: "multiple_choice", options: q.options?.length ? q.options : ["", ""], correct_answer: q.correct_answer || "" }
                      : { type: "short_answer" })}
                    className="text-xs rounded-full border border-[#EAE6DF] px-3 py-1.5 outline-none">
                    <option value="multiple_choice">Multiple choice</option>
                    <option value="short_answer">Written</option>
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-xs text-[#6B7280] flex items-center gap-1.5">Points
                    <input data-testid={`edit-q-points-${i}`} type="number" min="0" value={q.points}
                      onChange={(e) => upd(q.id, { points: e.target.value })}
                      className="w-16 rounded-lg border border-[#EAE6DF] px-2 py-1 outline-none focus:ring-2 focus:ring-[#81B29A]" />
                  </label>
                  <button data-testid={`edit-q-delete-${i}`} onClick={() => removeQ(q.id)} className="text-[#6B7280] hover:text-[#E07A5F]" title="Remove question"><Trash2 size={16} /></button>
                </div>
              </div>
              <textarea data-testid={`edit-q-text-${i}`} value={q.question} onChange={(e) => upd(q.id, { question: e.target.value })}
                placeholder="Question text…" className="w-full min-h-[70px] rounded-xl border border-[#EAE6DF] p-3 outline-none focus:ring-2 focus:ring-[#81B29A]" />

              {q.type === "multiple_choice" ? (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-[#6B7280]">Options — tap the circle to mark the correct answer</p>
                  {(q.options || []).map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <button data-testid={`edit-q-${i}-correct-${oi}`} onClick={() => upd(q.id, { correct_answer: opt })}
                        title="Mark correct" disabled={!opt.trim()}
                        className={`shrink-0 w-6 h-6 rounded-full border-2 grid place-items-center transition-colors ${q.correct_answer && q.correct_answer === opt ? "border-[#81B29A] bg-[#81B29A] text-white" : "border-[#EAE6DF] text-transparent"}`}>
                        <CheckCircle2 size={14} />
                      </button>
                      <input data-testid={`edit-q-${i}-option-${oi}`} value={opt} onChange={(e) => setOption(q.id, oi, e.target.value)}
                        placeholder={`Option ${oi + 1}`} className="flex-1 rounded-lg border border-[#EAE6DF] px-3 py-2 outline-none focus:ring-2 focus:ring-[#81B29A]" />
                      {(q.options || []).length > 2 && (
                        <button onClick={() => removeOption(q.id, oi)} className="text-[#6B7280] hover:text-[#E07A5F]"><Trash2 size={14} /></button>
                      )}
                    </div>
                  ))}
                  <button data-testid={`edit-q-${i}-add-option`} onClick={() => addOption(q.id)} className="flex items-center gap-1 text-xs text-[#81B29A] hover:underline"><Plus size={13} /> Add option</button>
                </div>
              ) : (
                <div className="mt-3">
                  <label className="text-xs text-[#6B7280]">Grading rubric (how the AI should grade this)</label>
                  <textarea data-testid={`edit-q-rubric-${i}`} value={q.rubric || ""} onChange={(e) => upd(q.id, { rubric: e.target.value })}
                    placeholder="e.g. Full marks for a thoughtful answer that names the main cause and explains why." className="w-full mt-1 min-h-[60px] rounded-xl border border-[#EAE6DF] p-3 outline-none focus:ring-2 focus:ring-[#81B29A]" />
                </div>
              )}
            </div>
          ))}
          {questions.length === 0 && <p className="text-[#6B7280] text-sm" data-testid="edit-no-q">No questions yet — add one above.</p>}
        </div>

        <div className="flex flex-wrap gap-3 mt-8">
          <button data-testid="edit-cancel-btn" onClick={() => navigate("/parent/manage-assignments")} className="rounded-full border border-[#EAE6DF] px-6 py-3 hover:border-[#3D405B] transition-colors">Cancel</button>
          <button data-testid="edit-save-btn" onClick={() => save(true)} disabled={saving}
            className="flex items-center gap-2 rounded-full bg-[#81B29A] text-white px-8 py-3 font-semibold hover:bg-[#6f9d87] transition-colors disabled:opacity-60"><Save size={18} /> {saving ? "Saving…" : "Save changes"}</button>
        </div>
      </main>
      <style>{`.inp{margin-top:.25rem;width:100%;border-radius:.75rem;border:1px solid #EAE6DF;background:#fff;padding:.6rem .9rem;outline:none}.inp:focus{box-shadow:0 0 0 2px #81B29A}`}</style>
    </div>
  );
}
